import { Label } from "semantic-ui-react";
import { fmtDateTime, roomLabel, roomOccupancyCounts, roomOccupancyState } from "./adminHelpers";

/* How many room chips a cell shows before the rest collapse into a "+N". Table
   rows are single-line (see DataTable), so one eight-room booking would
   otherwise widen the column for every other row on screen; the overflow chip
   carries the full list on hover instead, so nothing is only visible
   truncated. */
export const ROOM_CHIP_MAX = 4;

/* The actual type size and the dark-on-yellow contrast live in DataTable's
   `.bhu-room-chip` rule: Semantic's "small" (~11px, white on yellow) was a
   squint for the one piece of data this column exists to show. */
const CHIP_SIZE = "small";
const CHIP_CLASS = "bhu-room-chip";

/* Padding comes from DataTable's cell-label rule so every badge on the row
   shares it; here we only kill the default margin so the chips sit tight. */
const CHIP_STYLE = { margin: 0 };

/* One room's tooltip — its own state plus the stamps behind that state, since
   rooms on the same booking are routinely checked in hours apart. */
export function roomChipTitle(a) {
  const state = roomOccupancyState(a);
  const stamps = [
    a.actualCheckin ? `in ${fmtDateTime(a.actualCheckin)}` : "",
    a.actualCheckout ? `out ${fmtDateTime(a.actualCheckout)}` : "",
  ].filter(Boolean);
  const label = roomLabel(a.roomNumber, a.guestHouseShortName);
  return `Room ${label} — ${state.text}${stamps.length ? ` (${stamps.join(", ")})` : ""}`;
}

/* The count badge's tooltip, spelling out the split that "2/3 in" compresses. */
export function roomCountsTitle(counts) {
  const parts = [
    counts.checkedIn ? `${counts.checkedIn} checked in` : "",
    counts.awaiting ? `${counts.awaiting} awaiting check-in` : "",
    counts.checkedOut ? `${counts.checkedOut} checked out` : "",
    counts.noShow ? `${counts.noShow} no-show` : "",
  ].filter(Boolean);
  return `${counts.total} rooms — ${parts.join(", ")}`;
}

/* The admin table's "Room No" cell.

   A booking turns Checked In on its *first* arrival (see `checkin` in
   bookings/views.py), so a bare list of room numbers can't say who has actually
   turned up: a three-room booking with one guest in looked exactly like one
   with all three in. Each room wears its own state as a color here, and a
   multi-room booking whose arrivals have begun leads with how far along it is.

   Read-only labels throughout — the table row itself is clickable (it opens the
   booking) and must stay that way. Sorting and the search box still run on the
   column's accessor, not on this; only the presentation lives here. */
export default function RoomNumbersCell({ booking }) {
  const counts = roomOccupancyCounts(booking);
  /* counts is gated on the statuses that actually hold a room, so a Cancelled
     booking's leftover audit rows render as nothing, exactly as before. */
  const rooms = counts.total
    ? (booking.roomAssignments || []).filter((a) => a.roomNumber)
    : [];
  if (!rooms.length) return "—";

  const shown = rooms.slice(0, ROOM_CHIP_MAX);
  const hidden = rooms.slice(ROOM_CHIP_MAX);

  /* Nobody has arrived yet (a freshly confirmed booking) — "0/3 in" would be
     noise rather than news, and the all-yellow chips already say it. A
     single-room booking doesn't need it either: its one chip is the whole
     story. The badge stays uncolored whatever the split, like the other count
     badges on this screen — the chips beside it are what carry the color. */
  const showCount = counts.total > 1 && counts.arrived > 0;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      {showCount && (
        <Label
          circular
          size={CHIP_SIZE}
          className={CHIP_CLASS}
          style={CHIP_STYLE}
          title={roomCountsTitle(counts)}
        >
          {counts.checkedIn}/{counts.total} in
        </Label>
      )}
      {shown.map((a) => (
        <Label
          key={a.id ?? a.roomNumber}
          size={CHIP_SIZE}
          className={CHIP_CLASS}
          color={roomOccupancyState(a).color}
          style={CHIP_STYLE}
          title={roomChipTitle(a)}
        >
          {roomLabel(a.roomNumber, a.guestHouseShortName)}
        </Label>
      ))}
      {hidden.length > 0 && (
        <Label
          size={CHIP_SIZE}
          className={CHIP_CLASS}
          style={CHIP_STYLE}
          title={hidden.map(roomChipTitle).join("\n")}
        >
          +{hidden.length}
        </Label>
      )}
    </span>
  );
}
